import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import HeaderSecondary from 'flarum/forum/components/HeaderSecondary';
import SessionDropdown from 'flarum/forum/components/SessionDropdown';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import CommentPost from 'flarum/forum/components/CommentPost';
import LinkButton from 'flarum/common/components/LinkButton';
import MpHoverNavItem from './components/MpHoverNavItem';
import CartDropdown from './components/CartDropdown';
import { scanAndEmbed } from './embedScanner';
import ShopPage from './components/ShopPage';
import ProductPage from './components/ProductPage';
import CartPage from './components/CartPage';
import MarketplaceMessageNotification from './components/MarketplaceMessageNotification';
import ProductBumpedPost from './components/ProductBumpedPost';
import ProductRemovedNotification from './components/ProductRemovedNotification';
import addSellerProfileSection from './sellerProfileIntegration';
import addReviewsProfileTab from './reviewsProfileIntegration';
import SellerApprovedNotification from './components/SellerApprovedNotification';
import SellerVerifiedNotification from './components/SellerVerifiedNotification';
import SellerReviewApprovedNotification from './components/SellerReviewApprovedNotification';
import PlanActivatedNotification from './components/PlanActivatedNotification';
import PlanCancelledNotification from './components/PlanCancelledNotification';
import PlanExpiringNotification from './components/PlanExpiringNotification';
import PlanSubscribedAdminNotification from './components/PlanSubscribedAdminNotification';
import ProductVersionNotification from './components/ProductVersionNotification';
import PriceDropNotification from './components/PriceDropNotification';
import AbandonedCartNotification from './components/AbandonedCartNotification';
import { cartStore } from '../common/utils/cartStore';
import { cartPulse } from '../common/utils/cartPulse';

// Páginas fora do caminho de entrada (conta, checkout, curso, vendedor...) viram
// chunks por rota: só baixam quando a rota abre. Loja, produto e carrinho ficam no
// bundle principal porque são as páginas de entrada mais comuns. Sem webpackPrefetch,
// pelo mesmo motivo do Avocado: o loader da Flarum resolve a URL do chunk, não o runtime.
const CheckoutPage = () => import(/* webpackChunkName: 'forum/components/CheckoutPage', webpackMode: 'lazy-once' */ './components/CheckoutPage');
const CheckoutSuccessPage = () => import(/* webpackChunkName: 'forum/components/CheckoutSuccessPage', webpackMode: 'lazy-once' */ './components/CheckoutSuccessPage');
const MyOrdersPage = () => import(/* webpackChunkName: 'forum/components/MyOrdersPage', webpackMode: 'lazy-once' */ './components/MyOrdersPage');
const BundlePage = () => import(/* webpackChunkName: 'forum/components/BundlePage', webpackMode: 'lazy-once' */ './components/BundlePage');
const OrderDetailPage = () => import(/* webpackChunkName: 'forum/components/OrderDetailPage', webpackMode: 'lazy-once' */ './components/OrderDetailPage');
const DownloadsPage = () => import(/* webpackChunkName: 'forum/components/DownloadsPage', webpackMode: 'lazy-once' */ './components/DownloadsPage');
const MyCoursesPage = () => import(/* webpackChunkName: 'forum/components/MyCoursesPage', webpackMode: 'lazy-once' */ './components/MyCoursesPage');
const CoursePlayerPage = () => import(/* webpackChunkName: 'forum/components/CoursePlayerPage', webpackMode: 'lazy-once' */ './components/CoursePlayerPage');
const CoursesStorePage = () => import(/* webpackChunkName: 'forum/components/CoursesStorePage', webpackMode: 'lazy-once' */ './components/CoursesStorePage');
const MyProductsPage = () => import(/* webpackChunkName: 'forum/components/MyProductsPage', webpackMode: 'lazy-once' */ './components/MyProductsPage');
const MyCouponsPage = () => import(/* webpackChunkName: 'forum/components/MyCouponsPage', webpackMode: 'lazy-once' */ './components/MyCouponsPage');
const MyMessagesPage = () => import(/* webpackChunkName: 'forum/components/MyMessagesPage', webpackMode: 'lazy-once' */ './components/MyMessagesPage');
const ConversationPage = () => import(/* webpackChunkName: 'forum/components/ConversationPage', webpackMode: 'lazy-once' */ './components/ConversationPage');
const GiftCardsPage = () => import(/* webpackChunkName: 'forum/components/GiftCardsPage', webpackMode: 'lazy-once' */ './components/GiftCardsPage');
const MyWishlistPage = () => import(/* webpackChunkName: 'forum/components/MyWishlistPage', webpackMode: 'lazy-once' */ './components/MyWishlistPage');
const CollectionsPage = () => import(/* webpackChunkName: 'forum/components/CollectionsPage', webpackMode: 'lazy-once' */ './components/CollectionsPage');
const CollectionPage = () => import(/* webpackChunkName: 'forum/components/CollectionPage', webpackMode: 'lazy-once' */ './components/CollectionPage');
const TicketPage = () => import(/* webpackChunkName: 'forum/components/TicketPage', webpackMode: 'lazy-once' */ './components/TicketPage');
const PackagesPage = () => import(/* webpackChunkName: 'forum/components/PackagesPage', webpackMode: 'lazy-once' */ './components/PackagesPage');
const MySubscriptionsPage = () => import(/* webpackChunkName: 'forum/components/MySubscriptionsPage', webpackMode: 'lazy-once' */ './components/MySubscriptionsPage');
const MyMarketplacePage = () => import(/* webpackChunkName: 'forum/components/MyMarketplacePage', webpackMode: 'lazy-once' */ './components/MyMarketplacePage');
const MyStorePage = () => import(/* webpackChunkName: 'forum/components/MyStorePage', webpackMode: 'lazy-once' */ './components/MyStorePage');
const MyRefundRequestsPage = () => import(/* webpackChunkName: 'forum/components/MyRefundRequestsPage', webpackMode: 'lazy-once' */ './components/MyRefundRequestsPage');
const PlansPage = () => import(/* webpackChunkName: 'forum/components/PlansPage', webpackMode: 'lazy-once' */ './components/PlansPage');
const InviteClaimPage = () => import(/* webpackChunkName: 'forum/components/InviteClaimPage', webpackMode: 'lazy-once' */ './components/InviteClaimPage');

/**
 * Safety net global escopado ao marketplace. Só reage a rejections cujo XHR
 * tenha URL `/api/marketplace/*` — sem o filtro, qualquer falha de outra
 * extensão chega aqui e aparece como alert nosso.
 */
if (typeof window !== 'undefined' && !window.__mpUnhandledHooked) {
  window.__mpUnhandledHooked = true;
  window.addEventListener('unhandledrejection', event => {
    const reason = event.reason;
    const url = reason?.options?.url ?? reason?.xhr?.responseURL ?? '';
    if (!url || url.indexOf('/api/marketplace/') === -1) return;
    const status = reason?.status ?? 0;
    console.error('[marketplace unhandledrejection]', {
      status,
      error: reason?.response?.error,
      url,
      raw: reason
    });
    if (app?.alerts?.show && app?.translator) {
      const code = reason?.response?.error || 'unhandled_promise';
      const msg = app.translator.trans('ramon-marketplace.lib.errors.' + code);
      app.alerts.show({
        type: 'error',
        dismissible: true
      }, msg || 'Algo deu errado. Tente novamente.');
    }
  });
}
app.initializers.add('ramon-marketplace', () => {
  /**
   * Shop path dinâmico do admin (`Settings → URL da loja`).
   *
   * Boot order do Flarum 2.x — Application.boot():
   *   1. initialize()          ← initializers rodam AQUI
   *   2. store.pushPayload(…)
   *   3. this.forum = store.getById('forums','1')  ← app.forum nasce aqui
   *   4. mount()
   *
   * Conclusão: dentro deste initializer, `app.forum` é undefined. Ler
   * `app.forum.attribute(...)` lançaria TypeError, o try/catch engoliria,
   * e a rota cairia no fallback `/shop`. Era isso que mantinha a rota
   * "antiga" mesmo após salvar.
   *
   * Mesma fonte de dados, uma camada abaixo do model: `app.data.resources`
   * já está populado por `app.load(payload)` antes de boot(). O recurso
   * `forums` carrega os atributos serializeToForum.
   */
  let shopPath = '/shop';
  const forumResource = (app.data?.resources ?? []).find(r => r?.type === 'forums');
  const raw = forumResource?.attributes?.marketplace_shop_path;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed !== '' && trimmed !== '/') {
      shopPath = trimmed.startsWith('/') ? trimmed : '/' + trimmed;
    }
  }

  // Kill-switch do multivendedor: lido aqui (app.forum ainda não existe no
  // initializer; app.data.resources sim) para nem registrar as rotas/aba de
  // vendedor quando o admin desliga a feature. A API responde 404 em paralelo
  // (SellersGateMiddleware), e a loja não lista produtos de vendedor.
  const sellersEnabled = !!forumResource?.attributes?.marketplace_sellers_enabled;

  /*
   * Rotas canônicas + aliases legacy. Quando o admin troca o shop_path
   * (ex.: `/marketplace` → `/loja`), os PHP routes registram também `/shop`
   * como alias pra preservar bookmarks; o JS faz o mesmo mapping aqui.
   */
  app.routes['marketplace.shop'] = {
    path: shopPath,
    component: ShopPage
  };
  app.routes['marketplace.shop.category'] = {
    path: shopPath + '/category/:slug',
    component: ShopPage
  };
  if (shopPath !== '/shop') {
    app.routes['marketplace.shop.legacy'] = {
      path: '/shop',
      component: ShopPage
    };
    app.routes['marketplace.shop.legacy.category'] = {
      path: '/shop/category/:slug',
      component: ShopPage
    };
  }
  app.routes['marketplace.extension'] = {
    path: '/extension/:vendor/:name',
    component: ProductPage
  };
  app.routes['marketplace.item'] = {
    path: '/item/:slug',
    component: ProductPage
  };
  // Legacy — redireciona para URL canônica (/extension ou /item)
  app.routes['marketplace.product'] = {
    path: '/product/:id',
    component: ProductPage
  };
  app.routes['marketplace.cart'] = {
    path: '/cart',
    component: CartPage
  };
  app.routes['marketplace.checkout'] = {
    path: '/checkout',
    component: CheckoutPage
  };
  app.routes['marketplace.checkout.success'] = {
    path: '/checkout/success/:id',
    component: CheckoutSuccessPage
  };
  app.routes['marketplace.orders'] = {
    path: '/my/orders',
    component: MyOrdersPage
  };
  app.routes['marketplace.order.detail'] = {
    path: '/my/orders/:id',
    component: OrderDetailPage
  };
  app.routes['marketplace.downloads'] = {
    path: '/my/downloads',
    component: DownloadsPage
  };
  app.routes['marketplace.courses_store'] = {
    path: '/courses',
    component: CoursesStorePage
  };
  app.routes['marketplace.my_courses'] = {
    path: '/my/courses',
    component: MyCoursesPage
  };
  app.routes['marketplace.course'] = {
    path: '/my/courses/:id',
    component: CoursePlayerPage
  };
  app.routes['marketplace.ticket'] = {
    path: '/my/ticket/:id',
    component: TicketPage
  };
  app.routes['marketplace.packages'] = {
    path: '/my/packages',
    component: PackagesPage
  };
  app.routes['marketplace.subscriptions'] = {
    path: '/my/subscriptions',
    component: MySubscriptionsPage
  };
  app.routes['marketplace.wishlist'] = {
    path: '/my/wishlist',
    component: MyWishlistPage
  };
  app.routes['marketplace.collections'] = {
    path: '/collections',
    component: CollectionsPage
  };
  app.routes['marketplace.collection'] = {
    path: '/collection/:slug',
    component: CollectionPage
  };
  app.routes['marketplace.gift_cards'] = {
    path: '/gift-cards',
    component: GiftCardsPage
  };
  app.routes['marketplace.messages'] = {
    path: '/my/messages',
    component: MyMessagesPage
  };
  app.routes['marketplace.conversation'] = {
    path: '/my/messages/:id',
    component: ConversationPage
  };
  app.routes['marketplace.account'] = {
    path: '/my/marketplace',
    component: MyMarketplacePage
  };
  if (sellersEnabled) {
    app.routes['marketplace.store'] = {
      path: '/my/store',
      component: MyStorePage
    };
    app.routes['marketplace.my_products'] = {
      path: '/my/products',
      component: MyProductsPage
    };
    app.routes['marketplace.my_coupons'] = {
      path: '/my/coupons',
      component: MyCouponsPage
    };
  }
  app.routes['marketplace.refund_requests'] = {
    path: '/my/refund-requests',
    component: MyRefundRequestsPage
  };
  app.routes['marketplace.bundle'] = {
    path: '/bundle/:slug',
    component: BundlePage
  };
  app.routes['marketplace.plans'] = {
    path: '/plans',
    component: PlansPage
  };
  app.routes['marketplace.invite'] = {
    path: '/invite/marketplace/:token',
    component: InviteClaimPage
  };

  // Seção "Vendedor" (vitrine + avaliações) embutida no perfil do usuário.
  // Só registra a rota/aba quando o multivendedor está ligado.
  if (sellersEnabled) {
    addSellerProfileSection();
  }

  // Aba "Reviews" no perfil (avaliacoes escritas pelo usuario). Independente do
  // multivendedor; a aba se auto-oculta quando o usuario nao tem avaliacoes.
  addReviewsProfileTab();

  // Notificacoes in-app (alert). flarum/realtime as transmite automaticamente
  // pelo NotificationSyncer, sem precisar de Extend\Realtime.
  app.notificationComponents.sellerApproved = SellerApprovedNotification;
  app.notificationComponents.sellerVerified = SellerVerifiedNotification;
  app.notificationComponents.sellerReviewApproved = SellerReviewApprovedNotification;
  app.notificationComponents.planActivated = PlanActivatedNotification;
  app.notificationComponents.planCancelled = PlanCancelledNotification;
  app.notificationComponents.planExpiring = PlanExpiringNotification;
  app.notificationComponents.planSubscribedAdmin = PlanSubscribedAdminNotification;
  app.notificationComponents.productVersion = ProductVersionNotification;
  app.notificationComponents.priceDrop = PriceDropNotification;
  app.notificationComponents.abandonedCart = AbandonedCartNotification;
  app.notificationComponents.marketplaceMessage = MarketplaceMessageNotification;
  app.notificationComponents.marketplaceProductRemoved = ProductRemovedNotification;
  app.postComponents.marketplaceProductBumped = ProductBumpedPost;
  extend('flarum/forum/components/NotificationGrid', 'notificationTypes', function (items) {
    // "Alguém assina um plano (equipe)" é um aviso de bastidores que o backend
    // (PlanAccessService::notifyStaff) só envia a admins + o vendedor dono do
    // plano. marketplaceCanSell == hasPermission('marketplace.sell'), que é true
    // pra todo admin (admins têm toda permissão) e pros vendedores — exatamente
    // a "equipe". Buyer comum nunca recebe, então a preferência fica oculta.
    const canSeeStaffPlanNotice = !!app.session.user?.attribute('marketplaceCanSell');

    // "Minha loja é aprovada" só faz sentido com o multivendedor ligado
    // (mesmo kill-switch que gateia rotas/aba de vendedor acima).
    if (sellersEnabled) {
      items.add('sellerApproved', {
        name: 'sellerApproved',
        icon: 'fas fa-store',
        label: app.translator.trans('ramon-marketplace.forum.settings.notify_sellerApproved_label')
      });

      // "Minha loja é verificada" (selo): só interessa a quem pode vender, então
      // a preferência fica oculta pra comprador comum — coerente com o backend,
      // que só notifica donos com a permissão marketplace.sell.
      if (app.session.user?.attribute('marketplaceCanSell')) {
        items.add('sellerVerified', {
          name: 'sellerVerified',
          icon: 'fas fa-circle-check',
          label: app.translator.trans('ramon-marketplace.forum.settings.notify_sellerVerified_label')
        });
      }
    }
    items.add('sellerReviewApproved', {
      name: 'sellerReviewApproved',
      icon: 'fas fa-star',
      label: app.translator.trans('ramon-marketplace.forum.settings.notify_sellerReviewApproved_label')
    });
    items.add('planActivated', {
      name: 'planActivated',
      icon: 'fas fa-circle-check',
      label: app.translator.trans('ramon-marketplace.forum.settings.notify_planActivated_label')
    });
    items.add('planCancelled', {
      name: 'planCancelled',
      icon: 'fas fa-circle-xmark',
      label: app.translator.trans('ramon-marketplace.forum.settings.notify_planCancelled_label')
    });
    items.add('planExpiring', {
      name: 'planExpiring',
      icon: 'fas fa-clock',
      label: app.translator.trans('ramon-marketplace.forum.settings.notify_planExpiring_label')
    });
    if (canSeeStaffPlanNotice) {
      items.add('planSubscribedAdmin', {
        name: 'planSubscribedAdmin',
        icon: 'fas fa-user-plus',
        label: app.translator.trans('ramon-marketplace.forum.settings.notify_planSubscribedAdmin_label')
      });
    }
    items.add('productVersion', {
      name: 'productVersion',
      icon: 'fas fa-arrow-up-right-dots',
      label: app.translator.trans('ramon-marketplace.forum.settings.notify_productVersion_label')
    });
    items.add('priceDrop', {
      name: 'priceDrop',
      icon: 'fas fa-tags',
      label: app.translator.trans('ramon-marketplace.forum.settings.notify_priceDrop_label')
    });
    items.add('abandonedCart', {
      name: 'abandonedCart',
      icon: 'fas fa-cart-arrow-down',
      label: app.translator.trans('ramon-marketplace.forum.settings.notify_abandonedCart_label')
    });
    items.add('marketplaceMessage', {
      name: 'marketplaceMessage',
      icon: 'fas fa-comments',
      label: app.translator.trans('ramon-marketplace.forum.settings.notify_marketplaceMessage_label')
    });
  });

  // ── Helpers: construção dos items do marketplace ────────────────────
  // Filosofia: poluir ao mínimo a nav principal. "Loja" é link direto
  // sempre visível; "Minha conta" agrupa todos os meus-* via hover menu
  // (visualmente idêntico a um LinkButton). O hub unificado vive em
  // /my/marketplace — é o destino do clique no "Minha conta".
  const t = k => app.translator.trans('ramon-marketplace.forum.nav.' + k);

  // Visibilidade por item do menu — o admin liga/desliga cada entrada em
  // Settings → Navegação (serializada em `marketplace_nav_disabled`). Um item
  // só aparece se NÃO estiver na lista de desativados E (quando aplicável) sua
  // feature estiver ligada.
  const navDisabled = new Set(app.forum?.attribute('marketplace_nav_disabled') || []);
  const navOn = key => !navDisabled.has(key);

  // Shop é o único pill visível no nav; para usuários logados ele se
  // transforma em um MpHoverNavItem com submenu (Dashboard, Carrinho,
  // Pedidos, Downloads, Pacotes, Assinaturas). Guest vê só o link puro.
  const shopNavItem = () => {
    if (!app.session.user) {
      return m(LinkButton, {
        href: app.route('marketplace.shop'),
        icon: "fas fa-store"
      }, t('shop'));
    }
    return m(MpHoverNavItem, {
      href: app.route('marketplace.shop'),
      icon: "fas fa-store",
      label: t('shop'),
      subItems: [
      // Marketplace (hub) sempre primeiro — atalho rápido para a
      // página resumo do usuário (/my/marketplace).
      ...(navOn('marketplace') ? [{
        href: app.route('marketplace.account'),
        icon: 'fas fa-th-large',
        label: t('dashboard') || 'Marketplace'
      }] : []), ...(navOn('shop') ? [{
        href: app.route('marketplace.shop'),
        icon: 'fas fa-store',
        label: t('shop')
      }] : []), ...(app.forum?.attribute('marketplace_collections_enabled') !== false && navOn('collections') ? [{
        href: app.route('marketplace.collections'),
        icon: 'fas fa-layer-group',
        label: t('collections')
      }] : []), ...(app.forum?.attribute('marketplace_plans_enabled') && navOn('plans') ? [{
        href: app.route('marketplace.plans'),
        icon: 'fas fa-layer-group',
        label: t('plans') || 'Planos'
      }] : []), ...(navOn('cart') ? [{
        href: app.route('marketplace.cart'),
        icon: 'fas fa-shopping-cart',
        label: t('cart') || 'Carrinho',
        onclick: cartPulse().onclick
      }] : []), ...(navOn('orders') ? [{
        href: app.route('marketplace.orders'),
        icon: 'fas fa-receipt',
        label: t('my_orders')
      }] : []), ...(navOn('downloads') ? [{
        href: app.route('marketplace.downloads'),
        icon: 'fas fa-download',
        label: t('my_downloads')
      }] : []), {
        href: app.route('marketplace.wishlist'),
        icon: 'fas fa-heart',
        label: t('my_wishlist')
      }, ...(app.forum?.attribute('marketplace_sellers_enabled') && navOn('messages') ? [{
        href: app.route('marketplace.messages'),
        icon: 'fas fa-comments',
        label: t('messages')
      }] : []), ...(navOn('gift_cards') && app.forum?.attribute('marketplace_giftcards_enabled') !== false ? [{
        href: app.route('marketplace.gift_cards'),
        icon: 'fas fa-gift',
        label: t('gift_cards')
      }] : []), ...(app.forum?.attribute('marketplace_course_enabled') && navOn('courses_store') ? [{
        href: app.route('marketplace.courses_store'),
        icon: 'fas fa-graduation-cap',
        label: t('courses_store') || 'Cursos'
      }] : []), ...(app.forum?.attribute('marketplace_course_enabled') && navOn('my_courses') ? [{
        href: app.route('marketplace.my_courses'),
        icon: 'fas fa-play-circle',
        label: t('my_courses') || 'Meus cursos'
      }] : []), ...(app.forum?.attribute('marketplace_sellers_enabled') && navOn('store') ? [{
        href: app.route('marketplace.store'),
        icon: 'fas fa-store-alt',
        label: t('my_store') || 'Minhas vendas'
      }] : []), ...(app.forum?.attribute('marketplace_composer_enabled') && navOn('packages') ? [{
        href: app.route('marketplace.packages'),
        icon: 'fab fa-php',
        label: t('my_packages')
      }] : []), ...(navOn('subscriptions') ? [{
        href: app.route('marketplace.subscriptions'),
        icon: 'fas fa-sync-alt',
        label: t('my_subscriptions')
      }] : []), ...(app.forum?.attribute('marketplace_refund_requests_enabled') && navOn('refunds') ? [{
        href: app.route('marketplace.refund_requests'),
        icon: 'fas fa-undo-alt',
        label: t('my_refund_requests') || 'Reembolsos'
      }] : [])]
    });
  };

  // IndexSidebar:
  //   - desktop: pill "Loja" (MpHoverNavItem com submenu hover)
  //   - mobile : UM ÚNICO LinkButton "Marketplace" → /my/marketplace.
  //     Esse é o único atalho que aparece na lista do SelectDropdown
  //     mobile. O label dinâmico do botão Dropdown-toggle (Pedidos,
  //     Carrinho, etc.) é resolvido via override de `defaultLabel`
  //     mais abaixo, sem precisar registrar item por rota.
  extend(IndexSidebar.prototype, 'navItems', function (items) {
    items.add('marketplace-shop', shopNavItem(), 75);
    if (app.session.user && navOn('marketplace')) {
      items.add('marketplace-dashboard-mobile', m(LinkButton, {
        className: "MpNav-mobileItem",
        href: app.route('marketplace.account'),
        icon: "fas fa-th-large"
      }, t('dashboard') || 'Marketplace'), 74);
    }
  });

  // SessionDropdown (menu do avatar): UM ÚNICO link "Dashboard" pro hub
  // marketplace. O usuário viu sumir e pediu pra restaurar — sem isso,
  // ele não consegue chegar à área dele a partir do dropdown do avatar.
  extend(SessionDropdown.prototype, 'items', function (items) {
    if (!app.session.user || !navOn('marketplace')) return;
    items.add('marketplace-account', m(LinkButton, {
      href: app.route('marketplace.account'),
      icon: "fas fa-th-large"
    }, app.translator.trans('ramon-marketplace.forum.nav.dashboard') || 'Dashboard'), 85);
  });

  // HeaderSecondary: carrinho ao lado das notificações (icon-only, badge).
  extend(HeaderSecondary.prototype, 'items', function (items) {
    items.add('marketplace-cart', m(CartDropdown, null), 15);
  });

  // Página de Conta (engrenagem → Configurações): pill "Minha Loja" espelhando
  // exatamente como a extensão verified expõe o status — um pill colorido pelo
  // estado de aprovação (pendente/ativa/suspensa) que leva à área da loja, ou
  // o botão "Abrir minha loja" quando o membro pode vender mas ainda não abriu.
  extend('flarum/forum/components/SettingsPage', 'accountItems', function (items) {
    const user = app.session.user;
    if (!user) return;
    if (!app.forum?.attribute('marketplace_sellers_enabled')) return;
    const status = user.attribute('marketplaceStoreStatus') || null;
    const canSell = !!user.attribute('marketplaceCanSell');
    const ts = k => app.translator.trans('ramon-marketplace.forum.settings.' + k);
    const go = () => m.route.set(app.route('marketplace.store'));
    if (status) {
      const icon = status === 'active' ? 'fas fa-store' : status === 'pending' ? 'fas fa-hourglass-half' : 'fas fa-ban';
      items.add('marketplace-store', m("button", {
        type: "button",
        className: 'Button MpStoreSettings-pill MpStoreSettings-pill--' + status,
        onclick: go
      }, m("i", {
        className: 'Button-icon ' + icon
      }), m("span", {
        className: "Button-label"
      }, ts('store_' + status))), 81);
      return;
    }
    if (canSell) {
      items.add('marketplace-store', m("button", {
        type: "button",
        className: "Button MpStoreSettings-pill",
        onclick: go
      }, m("i", {
        className: "Button-icon fas fa-store"
      }), m("span", {
        className: "Button-label"
      }, ts('store_open'))), 81);
    }
  });

  // Media embed: escaneia o body de cada post e injeta cards de produto
  // pra qualquer URL do shop colada (igual Discord/Slack faz com YouTube).
  // Hookamos `oncreate` (primeira render) e `onupdate` (após edits/redraws).
  // Idempotente: scanAndEmbed marca <a data-mp-embedded> e não repete.
  // Embed scan é best-effort: se falhar (post deletado, network glitch),
  // não quebramos a renderização do post — só logamos pra debug.
  extend(CommentPost.prototype, 'oncreate', function (_vnode) {
    if (this.element) scanAndEmbed(this.element).catch(e => console.warn('[marketplace embed scan]', e));
  });
  extend(CommentPost.prototype, 'onupdate', function (_vnode) {
    if (this.element) scanAndEmbed(this.element).catch(e => console.warn('[marketplace embed scan]', e));
  });

  // Cart load no boot é best-effort — falha aqui só significa contador
  // do dropdown errado, e o usuário pode forçar refresh ao abrir o cart.
  // Defer para o próximo microtask: `app.session` e `app.forum` só são
  // populados DEPOIS que `initialize()` (e portanto este initializer)
  // retorna em `Application.boot()`. Chamar `app.request()` aqui dispara
  // o config extender de CSRF que lê `this.session.csrfToken` em sessão
  // ainda undefined → TypeError. O microtask roda após `boot()` terminar.
  Promise.resolve().then(() => cartStore.load()).catch(e => console.warn('[marketplace cart boot]', e));
});

// ─── Initializer com prioridade BAIXA (-300) ──────────────────────────
// O SelectDropdown mobile precisa do `defaultLabel` sobrescrito DEPOIS
// que o tema avocado-v2 (priority -200) seta `defaultLabel = "All
// Discussions"`. Como `extend()` empilha callbacks na ordem que foram
// registrados, registramos NOSSO extend num initializer que roda mais
// tarde — assim nosso callback executa por último em runtime e nosso
// label vence. Sem isso, em rotas marketplace o botão mostra sempre
// "All Discussions" (override do avocado).
function buildLabelRules(shopPath) {
  const esc = shopPath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return [[/^\/my\/orders(\/|$)/, 'Pedidos'], [/^\/my\/downloads(\/|$)/, 'Downloads'], [/^\/my\/packages(\/|$)/, 'Pacotes'], [/^\/my\/subscriptions(\/|$)/, 'Assinaturas'], [/^\/my\/marketplace(\/|$)/, 'Marketplace'], [/^\/my\/courses\/\d+/, 'Curso'], [/^\/my\/courses(\/|$)/, 'Meus cursos'], [/^\/courses(\/|$)/, 'Cursos'], [/^\/cart(\/|$)/, 'Carrinho'], [/^\/checkout\/success/, 'Pedido confirmado'], [/^\/checkout(\/|$)/, 'Checkout'], [/^\/bundle\//, 'Pacote'],
  // Produto: vira "Curso" quando a ProductPage publica type=course.
  [/^\/extension\//, courseLabelOr('Produto')], [/^\/item\//, courseLabelOr('Produto')], [/^\/product\//, courseLabelOr('Produto')], [new RegExp('^' + esc + '(\\/|$)'), 'Loja']];
}
function courseLabelOr(fallback) {
  return app.mpProductType === 'course' ? 'Curso' : fallback;
}
app.initializers.add('ramon-marketplace-late', () => {
  extend(IndexSidebar.prototype, 'items', function (items) {
    const nav = items.get('nav');
    if (!nav?.attrs) return;

    // Path do route Mithril atual (ex.: "/my/orders/25"). Fallback
    // pro window.location.pathname se m.route ainda não inicializou.
    const raw = typeof m !== 'undefined' && m.route?.get?.() || window.location.pathname || '';
    const path = String(raw).split('?')[0];

    // shopPath dinâmico — lido do bootstrap do SPA em todo render do
    // sidebar para que mudanças no admin reflitam imediatamente após
    // refresh, sem JS recompilado.
    const shopPathAttr = String(app.forum?.attribute('marketplace_shop_path') || '/shop');
    const shopPath = shopPathAttr.startsWith('/') ? shopPathAttr : '/' + shopPathAttr;
    const rules = buildLabelRules(shopPath);
    for (const [re, lbl] of rules) {
      if (re.test(path)) {
        nav.attrs.defaultLabel = lbl;
        return;
      }
    }
  });
}, -300);flarum.reg.addChunkModule('forum/components/CheckoutPage', './src/forum/components/CheckoutPage.tsx', 'ramon-marketplace', 'forum/components/CheckoutPage');
flarum.reg.addChunkModule('forum/components/CheckoutSuccessPage', './src/forum/components/CheckoutSuccessPage.tsx', 'ramon-marketplace', 'forum/components/CheckoutSuccessPage');
flarum.reg.addChunkModule('forum/components/MyOrdersPage', './src/forum/components/MyOrdersPage.tsx', 'ramon-marketplace', 'forum/components/MyOrdersPage');
flarum.reg.addChunkModule('forum/components/BundlePage', './src/forum/components/BundlePage.tsx', 'ramon-marketplace', 'forum/components/BundlePage');
flarum.reg.addChunkModule('forum/components/OrderDetailPage', './src/forum/components/OrderDetailPage.tsx', 'ramon-marketplace', 'forum/components/OrderDetailPage');
flarum.reg.addChunkModule('forum/components/DownloadsPage', './src/forum/components/DownloadsPage.tsx', 'ramon-marketplace', 'forum/components/DownloadsPage');
flarum.reg.addChunkModule('forum/components/MyCoursesPage', './src/forum/components/MyCoursesPage.tsx', 'ramon-marketplace', 'forum/components/MyCoursesPage');
flarum.reg.addChunkModule('forum/components/CoursePlayerPage', './src/forum/components/CoursePlayerPage.tsx', 'ramon-marketplace', 'forum/components/CoursePlayerPage');
flarum.reg.addChunkModule('forum/components/CoursesStorePage', './src/forum/components/CoursesStorePage.tsx', 'ramon-marketplace', 'forum/components/CoursesStorePage');
flarum.reg.addChunkModule('forum/components/MyProductsPage', './src/forum/components/MyProductsPage.tsx', 'ramon-marketplace', 'forum/components/MyProductsPage');
flarum.reg.addChunkModule('forum/components/MyCouponsPage', './src/forum/components/MyCouponsPage.tsx', 'ramon-marketplace', 'forum/components/MyCouponsPage');
flarum.reg.addChunkModule('forum/components/MyMessagesPage', './src/forum/components/MyMessagesPage.tsx', 'ramon-marketplace', 'forum/components/MyMessagesPage');
flarum.reg.addChunkModule('forum/components/ConversationPage', './src/forum/components/ConversationPage.tsx', 'ramon-marketplace', 'forum/components/ConversationPage');
flarum.reg.addChunkModule('forum/components/GiftCardsPage', './src/forum/components/GiftCardsPage.tsx', 'ramon-marketplace', 'forum/components/GiftCardsPage');
flarum.reg.addChunkModule('forum/components/MyWishlistPage', './src/forum/components/MyWishlistPage.tsx', 'ramon-marketplace', 'forum/components/MyWishlistPage');
flarum.reg.addChunkModule('forum/components/CollectionsPage', './src/forum/components/CollectionsPage.tsx', 'ramon-marketplace', 'forum/components/CollectionsPage');
flarum.reg.addChunkModule('forum/components/CollectionPage', './src/forum/components/CollectionPage.tsx', 'ramon-marketplace', 'forum/components/CollectionPage');
flarum.reg.addChunkModule('forum/components/TicketPage', './src/forum/components/TicketPage.tsx', 'ramon-marketplace', 'forum/components/TicketPage');
flarum.reg.addChunkModule('forum/components/PackagesPage', './src/forum/components/PackagesPage.tsx', 'ramon-marketplace', 'forum/components/PackagesPage');
flarum.reg.addChunkModule('forum/components/MySubscriptionsPage', './src/forum/components/MySubscriptionsPage.tsx', 'ramon-marketplace', 'forum/components/MySubscriptionsPage');
flarum.reg.addChunkModule('forum/components/MyMarketplacePage', './src/forum/components/MyMarketplacePage.tsx', 'ramon-marketplace', 'forum/components/MyMarketplacePage');
flarum.reg.addChunkModule('forum/components/MyStorePage', './src/forum/components/MyStorePage.tsx', 'ramon-marketplace', 'forum/components/MyStorePage');
flarum.reg.addChunkModule('forum/components/MyRefundRequestsPage', './src/forum/components/MyRefundRequestsPage.tsx', 'ramon-marketplace', 'forum/components/MyRefundRequestsPage');
flarum.reg.addChunkModule('forum/components/PlansPage', './src/forum/components/PlansPage.tsx', 'ramon-marketplace', 'forum/components/PlansPage');
flarum.reg.addChunkModule('forum/components/InviteClaimPage', './src/forum/components/InviteClaimPage.tsx', 'ramon-marketplace', 'forum/components/InviteClaimPage');
flarum.reg.addChunkModule('forum/components/CheckoutPage', './src/forum/components/CheckoutPage.tsx', 'ramon-marketplace', 'forum/utils/loginGuard');
flarum.reg.addChunkModule('forum/components/OrderDetailPage', './src/forum/components/OrderDetailPage.tsx', 'ramon-marketplace', 'forum/components/MpRefundRequestModal');
flarum.reg.addChunkModule('forum/components/MyProductsPage', './src/forum/components/MyProductsPage.tsx', 'ramon-marketplace', 'forum/components/SellerProductModal');
flarum.reg.addChunkModule('forum/components/MyMessagesPage', './src/forum/components/MyMessagesPage.tsx', 'ramon-marketplace', 'common/utils/maskSensitive');
flarum.reg.addChunkModule('forum/components/MyStorePage', './src/forum/components/MyStorePage.tsx', 'ramon-marketplace', 'forum/components/SellerAnalytics');